import type {
  BossDefinition,
  Distractor,
  Furniture,
  FurnitureKind,
  LightZone,
  NoiseZone,
  Point,
  Rect,
  StealthLevel,
} from './types';

export const STEALTH_WORLD_WIDTH = 960;
export const STEALTH_WORLD_HEIGHT = 540;

const startBottomLeft = { x: 54, y: 468 } as const;
const exitTopRight = { x: 872, y: 42, w: 48, h: 70 } as const;

function furniture(id: string, x: number, y: number, w: number, h: number, kind: FurnitureKind = 'desk'): Furniture {
  return { id, x, y, w, h, kind };
}

function boss(
  id: string,
  path: readonly Point[],
  speed = 72,
  visionDistance = 230,
  visionAngle = .9,
  pause = .55,
): BossDefinition {
  return { id, path, speed, visionDistance, visionAngle, pause };
}

function noise(id: string, x: number, y: number, w: number, h: number, label = '삐걱 바닥'): NoiseZone {
  return { id, x, y, w, h, label };
}

function light(id: string, x: number, y: number, w: number, h: number, cycle = 3.2, onTime = 1.8, phase = 0): LightZone {
  return { id, x, y, w, h, cycle, onTime, phase };
}

function copier(id: string, x: number, y: number, range = 360, label = '복사기'): Distractor {
  return { id, x, y, w: 54, h: 56, range, label };
}

function shadow(x: number, y: number, w: number, h: number): Rect {
  return { x, y, w, h };
}

function level(
  input: Omit<StealthLevel, 'start' | 'exit' | 'noiseZones' | 'lightZones' | 'distractors' | 'shadows'> & Partial<Pick<StealthLevel, 'start' | 'exit' | 'noiseZones' | 'lightZones' | 'distractors' | 'shadows'>>,
): StealthLevel {
  return {
    ...input,
    start: input.start ?? startBottomLeft,
    exit: input.exit ?? exitTopRight,
    noiseZones: input.noiseZones ?? [],
    lightZones: input.lightZones ?? [],
    distractors: input.distractors ?? [],
    shadows: input.shadows ?? [],
  };
}

export const STEALTH_LEVELS: readonly StealthLevel[] = [
  level({
    number: 1,
    title: '마지막 한 명',
    subtitle: '책상은 사장님의 시야를 완전히 막아줍니다.',
    clearCopy: '첫 번째 눈치를 무사히 통과했습니다.',
    targetSeconds: 18,
    detectionRate: .58,
    furniture: [
      furniture('l1-desk-a', 150, 356, 230, 62),
      furniture('l1-desk-b', 430, 346, 220, 62),
      furniture('l1-desk-c', 620, 152, 215, 62),
      furniture('l1-cabinet', 270, 100, 205, 58, 'cabinet'),
    ],
    bosses: [boss('l1-boss', [{ x: 520, y: 116 }, { x: 520, y: 470 }], 62, 220, .86)],
    shadows: [shadow(28, 244, 110, 220)],
  }),
  level({
    number: 2,
    title: '복사기는 내 편',
    subtitle: '가까이에서 E를 누르면 사장님이 소리를 확인하러 갑니다.',
    clearCopy: '복사 한 장으로 시선을 완벽히 돌렸습니다.',
    targetSeconds: 21,
    detectionRate: .6,
    furniture: [
      furniture('l2-desk-a', 120, 330, 250, 58),
      furniture('l2-desk-b', 470, 350, 245, 58),
      furniture('l2-partition', 420, 110, 50, 165, 'partition'),
      furniture('l2-sofa', 700, 160, 140, 58, 'sofa'),
    ],
    bosses: [boss('l2-boss', [{ x: 735, y: 440 }, { x: 735, y: 118 }, { x: 525, y: 118 }], 70, 235, .92)],
    distractors: [copier('l2-copy', 390, 425, 390)],
    shadows: [shadow(38, 80, 120, 175)],
  }),
  level({
    number: 3,
    title: '삐걱대는 복도',
    subtitle: '주황 바닥은 Shift로 살금살금 지나가세요.',
    clearCopy: '바닥보다 조용하게 복도를 건넜습니다.',
    targetSeconds: 20,
    detectionRate: .62,
    furniture: [
      furniture('l3-wall-a', 195, 75, 55, 310, 'partition'),
      furniture('l3-wall-b', 470, 155, 55, 310, 'partition'),
      furniture('l3-desk', 650, 300, 205, 60),
      furniture('l3-cabinet', 640, 95, 180, 55, 'cabinet'),
    ],
    bosses: [boss('l3-boss', [{ x: 355, y: 100 }, { x: 355, y: 450 }], 74, 225, .88)],
    noiseZones: [noise('l3-squeak-a', 270, 400, 180, 62), noise('l3-squeak-b', 545, 188, 82, 155)],
    shadows: [shadow(32, 86, 132, 175)],
  }),
  level({
    number: 4,
    title: '센서등은 퇴근 금지',
    subtitle: '노란 불이 꺼질 때마다 한 구간씩 이동하세요.',
    clearCopy: '센서등의 박자를 읽고 어둠만 골라 밟았습니다.',
    targetSeconds: 24,
    detectionRate: .6,
    furniture: [
      furniture('l4-desk-a', 155, 330, 210, 58),
      furniture('l4-desk-b', 570, 330, 220, 58),
      furniture('l4-cabinet-a', 310, 118, 165, 55, 'cabinet'),
      furniture('l4-cabinet-b', 690, 115, 150, 55, 'cabinet'),
    ],
    bosses: [boss('l4-boss', [{ x: 485, y: 105 }, { x: 485, y: 455 }], 66, 220, .86)],
    lightZones: [light('l4-light-a', 380, 360, 155, 108, 3.1, 1.65), light('l4-light-b', 520, 160, 142, 115, 2.8, 1.5, .8)],
    shadows: [shadow(34, 94, 92, 275)],
  }),
  level({
    number: 5,
    title: '책상 숲',
    subtitle: '두 사장님의 시야 사이에서 책상을 하나씩 옮겨 타세요.',
    clearCopy: '책상 숲의 사각지대를 모두 찾아냈습니다.',
    targetSeconds: 25,
    detectionRate: .64,
    furniture: [
      furniture('l5-desk-a', 130, 385, 175, 54),
      furniture('l5-desk-b', 360, 375, 175, 54),
      furniture('l5-desk-c', 590, 365, 175, 54),
      furniture('l5-desk-d', 215, 190, 175, 54),
      furniture('l5-desk-e', 500, 175, 175, 54),
      furniture('l5-cabinet', 735, 105, 110, 50, 'cabinet'),
    ],
    bosses: [
      boss('l5-boss-a', [{ x: 120, y: 280 }, { x: 830, y: 280 }], 78, 210, .82),
      boss('l5-boss-b', [{ x: 750, y: 90 }, { x: 750, y: 455 }], 64, 205, .82),
    ],
    shadows: [shadow(38, 118, 120, 155)],
  }),
  level({
    number: 6,
    title: '긴급 회의실',
    subtitle: '회의 테이블을 크게 돌아가는 편이 더 빠를 수 있습니다.',
    clearCopy: '회의 없이 회의실을 빠져나왔습니다.',
    targetSeconds: 27,
    detectionRate: .65,
    furniture: [
      furniture('l6-table', 260, 176, 440, 150),
      furniture('l6-cabinet-a', 110, 130, 100, 55, 'cabinet'),
      furniture('l6-cabinet-b', 750, 340, 110, 55, 'cabinet'),
      furniture('l6-plant-a', 175, 390, 50, 50, 'plant'),
      furniture('l6-plant-b', 735, 90, 50, 50, 'plant'),
    ],
    bosses: [
      boss('l6-boss-a', [{ x: 235, y: 90 }, { x: 700, y: 90 }, { x: 715, y: 460 }, { x: 235, y: 460 }], 82, 235, .9, .35),
    ],
    distractors: [copier('l6-copy', 105, 255, 420)],
  }),
  level({
    number: 7,
    title: '소음과 빛 사이',
    subtitle: '조용한 길은 밝고, 어두운 길은 시끄럽습니다.',
    clearCopy: '두 가지 위험 중 더 나은 타이밍을 골랐습니다.',
    targetSeconds: 29,
    detectionRate: .66,
    furniture: [
      furniture('l7-divider', 430, 80, 62, 365, 'partition'),
      furniture('l7-desk-a', 110, 220, 230, 58),
      furniture('l7-desk-b', 600, 330, 220, 58),
      furniture('l7-cabinet', 650, 115, 170, 55, 'cabinet'),
    ],
    bosses: [boss('l7-boss', [{ x: 560, y: 105 }, { x: 560, y: 455 }, { x: 850, y: 455 }], 78, 235, .92)],
    noiseZones: [noise('l7-noise', 200, 330, 200, 105, '낡은 카펫')],
    lightZones: [light('l7-light', 500, 205, 200, 90, 3, 1.65, .35)],
    shadows: [shadow(40, 80, 95, 130)],
  }),
  level({
    number: 8,
    title: '복사기 릴레이',
    subtitle: '두 복사기를 차례로 울려 순찰 경로를 비우세요.',
    clearCopy: '사장님은 복사기만 보고, 나는 문만 봤습니다.',
    targetSeconds: 30,
    detectionRate: .66,
    furniture: [
      furniture('l8-desk-a', 180, 360, 225, 58),
      furniture('l8-wall', 460, 145, 55, 290, 'partition'),
      furniture('l8-desk-b', 590, 190, 235, 58),
      furniture('l8-sofa', 620, 390, 170, 55, 'sofa'),
    ],
    bosses: [
      boss('l8-boss-a', [{ x: 330, y: 105 }, { x: 330, y: 300 }, { x: 600, y: 300 }], 74, 225, .88),
      boss('l8-boss-b', [{ x: 735, y: 95 }, { x: 735, y: 465 }], 68, 215, .86),
    ],
    distractors: [copier('l8-copy-a', 100, 180, 380), copier('l8-copy-b', 540, 430, 380)],
  }),
  level({
    number: 9,
    title: '문 앞의 사각지대',
    subtitle: '퇴근 문 가까이일수록 더 오래 기다려야 합니다.',
    clearCopy: '마지막 두 걸음을 가장 침착하게 옮겼습니다.',
    targetSeconds: 27,
    detectionRate: .7,
    furniture: [
      furniture('l9-desk-a', 135, 345, 250, 62),
      furniture('l9-desk-b', 470, 300, 235, 62),
      furniture('l9-cabinet', 730, 170, 130, 58, 'cabinet'),
      furniture('l9-plant', 805, 300, 50, 50, 'plant'),
    ],
    bosses: [
      boss('l9-boss-a', [{ x: 450, y: 115 }, { x: 450, y: 450 }], 76, 235, .9),
      boss('l9-door-boss', [{ x: 780, y: 120 }, { x: 875, y: 260 }, { x: 720, y: 280 }], 58, 205, 1.02, .75),
    ],
    lightZones: [light('l9-door-light', 820, 115, 110, 205, 3.4, 2, .7)],
    shadows: [shadow(680, 42, 92, 115)],
  }),
  level({
    number: 10,
    title: '첫 야근 순찰',
    subtitle: '세 갈래 순찰을 한 번에 읽어야 합니다.',
    clearCopy: '야근 순찰표보다 한 박자 먼저 움직였습니다.',
    targetSeconds: 32,
    detectionRate: .7,
    furniture: [
      furniture('l10-desk-a', 120, 385, 195, 55),
      furniture('l10-desk-b', 385, 370, 195, 55),
      furniture('l10-desk-c', 650, 355, 195, 55),
      furniture('l10-desk-d', 245, 160, 195, 55),
      furniture('l10-desk-e', 555, 145, 195, 55),
    ],
    bosses: [
      boss('l10-boss-a', [{ x: 95, y: 275 }, { x: 855, y: 275 }], 92, 225, .84, .25),
      boss('l10-boss-b', [{ x: 485, y: 85 }, { x: 485, y: 465 }], 74, 220, .86),
    ],
    noiseZones: [noise('l10-noise', 700, 430, 140, 65, '야근 카트')],
    distractors: [copier('l10-copy', 92, 86, 430)],
  }),
  level({
    number: 11,
    title: '불 꺼진 인사팀',
    subtitle: '어두운 자리는 안전하지만 출구까지 길게 돌아갑니다.',
    clearCopy: '인사팀의 그림자만 골라 밟았습니다.',
    targetSeconds: 34,
    detectionRate: .72,
    furniture: [
      furniture('l11-wall-a', 220, 75, 55, 315, 'partition'),
      furniture('l11-wall-b', 500, 150, 55, 320, 'partition'),
      furniture('l11-desk-a', 610, 360, 230, 58),
      furniture('l11-cabinet', 640, 105, 190, 55, 'cabinet'),
    ],
    bosses: [
      boss('l11-boss-a', [{ x: 360, y: 95 }, { x: 360, y: 450 }], 82, 240, .9),
      boss('l11-boss-b', [{ x: 610, y: 265 }, { x: 870, y: 265 }], 74, 220, .86),
    ],
    lightZones: [light('l11-light-a', 280, 190, 205, 90, 2.8, 1.75), light('l11-light-b', 560, 195, 180, 95, 3.1, 1.8, 1.1)],
    shadows: [shadow(34, 70, 155, 360), shadow(740, 300, 100, 120)],
  }),
  level({
    number: 12,
    title: '서류 카트의 배신',
    subtitle: '카트 옆을 달리면 멀리 있는 사장님도 돌아봅니다.',
    clearCopy: '서류 한 장 흔들지 않고 통과했습니다.',
    targetSeconds: 31,
    detectionRate: .72,
    furniture: [
      furniture('l12-desk-a', 135, 340, 225, 60),
      furniture('l12-desk-b', 430, 340, 225, 60),
      furniture('l12-desk-c', 260, 135, 225, 60),
      furniture('l12-cabinet', 680, 125, 160, 55, 'cabinet'),
    ],
    bosses: [boss('l12-boss', [{ x: 750, y: 440 }, { x: 750, y: 78 }, { x: 520, y: 78 }], 84, 250, .94)],
    noiseZones: [noise('l12-cart-a', 380, 420, 150, 60, '서류 카트'), noise('l12-cart-b', 505, 210, 150, 65, '금속 서랍')],
    shadows: [shadow(40, 85, 110, 175)],
  }),
  level({
    number: 13,
    title: '유리 회의실',
    subtitle: '가운데 회의실은 가려주지 않습니다. 바깥 가구를 이용하세요.',
    clearCopy: '투명한 회의실 바깥으로 완벽히 숨었습니다.',
    targetSeconds: 35,
    detectionRate: .74,
    furniture: [
      furniture('l13-table', 315, 175, 330, 150),
      furniture('l13-cabinet-a', 110, 125, 120, 55, 'cabinet'),
      furniture('l13-cabinet-b', 725, 345, 120, 55, 'cabinet'),
      furniture('l13-sofa-a', 110, 365, 145, 55, 'sofa'),
      furniture('l13-sofa-b', 700, 105, 145, 55, 'sofa'),
    ],
    bosses: [
      boss('l13-boss-a', [{ x: 265, y: 95 }, { x: 695, y: 95 }, { x: 695, y: 440 }, { x: 265, y: 440 }], 88, 250, .96, .2),
      boss('l13-boss-b', [{ x: 475, y: 125 }, { x: 475, y: 415 }], 66, 220, .88),
    ],
    lightZones: [light('l13-glass-light', 300, 160, 360, 180, 3.5, 2.4, .5)],
    distractors: [copier('l13-copy', 80, 250, 390)],
  }),
  level({
    number: 14,
    title: '세 번의 기침',
    subtitle: '소음 구역을 하나씩 지나며 순찰이 원위치로 돌아오길 기다리세요.',
    clearCopy: '기침 소리 사이로 발소리를 감췄습니다.',
    targetSeconds: 36,
    detectionRate: .75,
    furniture: [
      furniture('l14-desk-a', 150, 390, 185, 54),
      furniture('l14-desk-b', 390, 335, 185, 54),
      furniture('l14-desk-c', 635, 280, 185, 54),
      furniture('l14-cabinet-a', 250, 120, 180, 52, 'cabinet'),
      furniture('l14-cabinet-b', 600, 95, 180, 52, 'cabinet'),
    ],
    bosses: [
      boss('l14-boss-a', [{ x: 120, y: 260 }, { x: 845, y: 260 }], 96, 230, .86),
      boss('l14-boss-b', [{ x: 510, y: 95 }, { x: 510, y: 460 }], 78, 225, .86),
    ],
    noiseZones: [
      noise('l14-noise-a', 335, 420, 75, 70, '빈 페트병'),
      noise('l14-noise-b', 575, 365, 72, 72, '철제 바닥'),
      noise('l14-noise-c', 790, 335, 70, 70, '택배 상자'),
    ],
    shadows: [shadow(40, 75, 110, 170)],
  }),
  level({
    number: 15,
    title: '점멸하는 탕비실',
    subtitle: '서로 다른 박자의 센서등 세 개를 통과하세요.',
    clearCopy: '탕비실 불빛이 모두 꺼진 한순간을 잡았습니다.',
    targetSeconds: 38,
    detectionRate: .76,
    furniture: [
      furniture('l15-counter-a', 160, 350, 200, 62, 'cabinet'),
      furniture('l15-counter-b', 430, 285, 200, 62, 'cabinet'),
      furniture('l15-counter-c', 690, 220, 150, 62, 'cabinet'),
      furniture('l15-desk', 275, 110, 220, 55),
    ],
    bosses: [boss('l15-boss', [{ x: 630, y: 90 }, { x: 630, y: 460 }], 84, 250, .94)],
    lightZones: [
      light('l15-light-a', 365, 380, 110, 100, 2.5, 1.55),
      light('l15-light-b', 555, 300, 115, 115, 3.1, 1.8, .8),
      light('l15-light-c', 730, 115, 120, 110, 2.2, 1.3, .35),
    ],
    distractors: [copier('l15-coffee', 100, 160, 360, '커피머신')],
  }),
  level({
    number: 16,
    title: '복도 교차 순찰',
    subtitle: '가로와 세로 순찰이 만나는 중앙을 피해서 돌아가세요.',
    clearCopy: '두 순찰이 마주 보는 순간 반대편으로 빠졌습니다.',
    targetSeconds: 37,
    detectionRate: .78,
    furniture: [
      furniture('l16-block-a', 220, 115, 190, 110, 'partition'),
      furniture('l16-block-b', 550, 115, 190, 110, 'partition'),
      furniture('l16-block-c', 220, 330, 190, 110, 'partition'),
      furniture('l16-block-d', 550, 330, 190, 110, 'partition'),
    ],
    bosses: [
      boss('l16-boss-a', [{ x: 85, y: 275 }, { x: 875, y: 275 }], 105, 235, .84, .18),
      boss('l16-boss-b', [{ x: 480, y: 70 }, { x: 480, y: 470 }], 90, 235, .84, .18),
    ],
    noiseZones: [noise('l16-center', 425, 225, 110, 105, '광택 바닥')],
    shadows: [shadow(45, 82, 105, 140), shadow(790, 340, 110, 115)],
  }),
  level({
    number: 17,
    title: '가짜 퇴근 방송',
    subtitle: '복사기를 울리면 세 명이 동시에 움직입니다.',
    clearCopy: '모두가 소리를 확인하는 동안 진짜로 퇴근했습니다.',
    targetSeconds: 39,
    detectionRate: .78,
    furniture: [
      furniture('l17-desk-a', 145, 355, 205, 58),
      furniture('l17-desk-b', 390, 355, 205, 58),
      furniture('l17-desk-c', 635, 355, 205, 58),
      furniture('l17-desk-d', 270, 155, 205, 58),
      furniture('l17-desk-e', 545, 145, 205, 58),
    ],
    bosses: [
      boss('l17-boss-a', [{ x: 105, y: 265 }, { x: 855, y: 265 }], 92, 225, .86),
      boss('l17-boss-b', [{ x: 400, y: 80 }, { x: 400, y: 455 }], 78, 220, .86),
      boss('l17-boss-c', [{ x: 735, y: 80 }, { x: 735, y: 455 }], 72, 215, .84),
    ],
    distractors: [copier('l17-copy', 90, 92, 520, '사내 방송 복합기')],
    lightZones: [light('l17-exit-light', 785, 70, 140, 150, 3.2, 1.9, .6)],
  }),
  level({
    number: 18,
    title: '사장실 앞 3미터',
    subtitle: '짧은 거리지만 시야가 겹칩니다. 그림자에서 타이밍을 만드세요.',
    clearCopy: '사장실 문 앞을 아무 일도 없던 것처럼 지났습니다.',
    targetSeconds: 40,
    detectionRate: .8,
    start: { x: 55, y: 450 },
    exit: { x: 865, y: 230, w: 52, h: 72 },
    furniture: [
      furniture('l18-office', 640, 65, 265, 130, 'partition'),
      furniture('l18-desk-a', 140, 340, 225, 60),
      furniture('l18-desk-b', 420, 325, 170, 60),
      furniture('l18-cabinet-a', 235, 145, 165, 55, 'cabinet'),
      furniture('l18-cabinet-b', 480, 135, 130, 55, 'cabinet'),
    ],
    bosses: [
      boss('l18-boss-a', [{ x: 600, y: 235 }, { x: 860, y: 235 }, { x: 860, y: 430 }, { x: 600, y: 430 }], 82, 250, 1.02, .35),
      boss('l18-boss-b', [{ x: 115, y: 265 }, { x: 585, y: 265 }], 92, 235, .9),
      boss('l18-boss-c', [{ x: 455, y: 75 }, { x: 455, y: 290 }], 68, 220, .9),
    ],
    shadows: [shadow(42, 82, 125, 150), shadow(600, 315, 92, 100)],
    noiseZones: [noise('l18-noise', 700, 320, 150, 72, '사장실 앞 마룻바닥')],
  }),
  level({
    number: 19,
    title: '모든 불이 켜진 밤',
    subtitle: '빛, 소음, 세 순찰을 한 번에 피해 마지막 복도로 들어가세요.',
    clearCopy: '밝은 사무실에서도 그림자처럼 움직였습니다.',
    targetSeconds: 43,
    detectionRate: .82,
    furniture: [
      furniture('l19-desk-a', 135, 390, 180, 55),
      furniture('l19-desk-b', 370, 365, 180, 55),
      furniture('l19-desk-c', 605, 340, 180, 55),
      furniture('l19-desk-d', 245, 165, 180, 55),
      furniture('l19-desk-e', 515, 140, 180, 55),
      furniture('l19-cabinet', 740, 105, 115, 52, 'cabinet'),
    ],
    bosses: [
      boss('l19-boss-a', [{ x: 90, y: 285 }, { x: 865, y: 285 }], 108, 240, .86, .15),
      boss('l19-boss-b', [{ x: 340, y: 75 }, { x: 340, y: 465 }], 88, 230, .86, .2),
      boss('l19-boss-c', [{ x: 700, y: 75 }, { x: 700, y: 465 }], 84, 230, .86, .2),
    ],
    noiseZones: [noise('l19-noise-a', 555, 420, 100, 70, '금속 통로'), noise('l19-noise-b', 770, 190, 100, 70, '택배 구역')],
    lightZones: [
      light('l19-light-a', 315, 285, 130, 95, 2.6, 1.65),
      light('l19-light-b', 560, 230, 135, 95, 3, 1.9, .6),
      light('l19-light-c', 790, 115, 130, 105, 2.4, 1.5, 1.1),
    ],
    distractors: [copier('l19-copy', 85, 105, 440)],
    shadows: [shadow(36, 320, 72, 125)],
  }),
  level({
    number: 20,
    title: '진짜 칼퇴',
    subtitle: '지금까지 배운 은폐, 살금, 교란, 대기를 전부 사용하세요.',
    clearCopy: '아무에게도 들키지 않고 진짜 칼퇴에 성공했습니다.',
    targetSeconds: 46,
    detectionRate: .84,
    furniture: [
      furniture('l20-desk-a', 120, 400, 180, 54),
      furniture('l20-desk-b', 350, 375, 180, 54),
      furniture('l20-desk-c', 585, 350, 180, 54),
      furniture('l20-desk-d', 220, 190, 185, 54),
      furniture('l20-desk-e', 480, 165, 185, 54),
      furniture('l20-cabinet-a', 740, 125, 120, 52, 'cabinet'),
      furniture('l20-partition', 810, 270, 45, 165, 'partition'),
    ],
    bosses: [
      boss('l20-boss-a', [{ x: 90, y: 295 }, { x: 875, y: 295 }], 112, 250, .88, .12),
      boss('l20-boss-b', [{ x: 325, y: 75 }, { x: 325, y: 470 }], 94, 238, .88, .15),
      boss('l20-boss-c', [{ x: 680, y: 75 }, { x: 680, y: 470 }], 90, 238, .88, .15),
      boss('l20-door-boss', [{ x: 850, y: 85 }, { x: 850, y: 255 }, { x: 760, y: 255 }], 70, 220, 1, .45),
    ],
    noiseZones: [noise('l20-noise-a', 300, 430, 80, 65, '빈 캔'), noise('l20-noise-b', 735, 360, 72, 72, '철제 서랍')],
    lightZones: [
      light('l20-light-a', 420, 330, 130, 105, 2.5, 1.6),
      light('l20-light-b', 640, 210, 130, 105, 2.9, 1.8, .8),
      light('l20-exit-light', 810, 70, 120, 160, 2.3, 1.45, .35),
    ],
    distractors: [copier('l20-copy-a', 85, 125, 430), copier('l20-copy-b', 550, 445, 430)],
    shadows: [shadow(36, 315, 70, 130), shadow(420, 75, 115, 70)],
  }),
];

export const STEALTH_LEVEL_COUNT = STEALTH_LEVELS.length;
export const STEALTH_LEVEL_TITLES = STEALTH_LEVELS.map((item) => item.title);
